"use client";

import { usePostHog } from "posthog-js/react";
import { useCallback, useContext } from "react";
import NeedPaidPlan from "../components/NeedPaidPlan";
import {
  CancelledAccessResponse,
  isCancelledAccessGated,
  mayBeLapsed,
} from "../helpers/cancelledAccess";
import { ModalContext } from "./useModal";
import { useProfile } from "./useProfile";
import { useSubscriptions } from "./useSubscriptions";

/**
 * Whether the cancelled-user gate locks this user's clips
 * (helpers/cancelledAccess.ts). `isLoading` stays true only while a
 * possibly-lapsed user's GET /billing/subscription is in flight, so pages
 * that lock entirely can wait instead of flashing the clip; paying users
 * never wait.
 */
export const useCancelledAccess = () => {
  const { data: profile, isLoading: profileIsLoading } = useProfile();
  const { data, isLoading: subscriptionsAreLoading } = useSubscriptions();
  const response = data as CancelledAccessResponse | undefined;
  return {
    gated: isCancelledAccessGated(profile, response),
    state: response?.cancelled_access ?? null,
    isLoading:
      profileIsLoading || (mayBeLapsed(profile) && subscriptionsAreLoading),
  };
};

/**
 * The resubscribe dialog, logged as "Cancelled User Wall Showed".
 */
export const useOpenResubscribeWall = () => {
  const modal = useContext(ModalContext);
  const posthog = usePostHog();
  const { state } = useCancelledAccess();
  return useCallback(
    (trigger: string) => {
      posthog?.capture("Cancelled User Wall Showed", {
        trigger,
        previous_plan: state?.plan ?? null,
      });
      modal.open(<NeedPaidPlan variant="subscription_ended" />, {
        className: "max-w-6xl",
      });
    },
    [modal, posthog, state?.plan],
  );
};

/**
 * For handlers that create, play or download a clip: opens the resubscribe
 * dialog and returns true when the gate covers this user, false otherwise.
 */
export const useCancelledAccessGuard = () => {
  const { gated } = useCancelledAccess();
  const openResubscribeWall = useOpenResubscribeWall();
  return useCallback(
    (trigger: string) => {
      if (!gated) return false;
      openResubscribeWall(trigger);
      return true;
    },
    [gated, openResubscribeWall],
  );
};
